import { useState, useEffect } from 'react'
import { isNull, mergeWith, omit } from 'lodash-es'
import { GetState, SetState } from 'zustand'

interface Arguments<T extends object> {
  data: Partial<T>
  setState: SetState<T>
  getState: GetState<T>
  dependency: string | number
  omitFields?: (state: T) => (keyof T)[]
  /** Adjust the fresh data to the current state before it is merged (e.g. keep user filters). */
  transformData?: (data: Partial<T>, state: T) => Partial<T>
}

const useStoreReinitializer = <T extends object>({
  data,
  setState,
  getState,
  dependency,
  omitFields,
  transformData,
}: Arguments<T>) => {
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    const currentState = getState()

    const transformed = transformData ? transformData(data, currentState) : data
    const omittedData = omit(transformed, omitFields ? omitFields(currentState) : [])

    const newState = mergeWith(omittedData, currentState, (stateOfProps, currentState) =>
      isNull(stateOfProps) ? currentState : stateOfProps,
    )

    setState(newState)
    setInitialized(true)
  }, [dependency])

  return initialized
}

export default useStoreReinitializer
